/**
 * DayCell Component
 *
 * Renders a single day in the calendar month grid. Displays the day number,
 * up to 3 BookingIndicator pills, and an overflow label when there are more
 * than 3 bookings on a given day.
 *
 * Validates: Requirements 2.1, 2.5, 2.6, 2.7
 * - Requirement 2.1: Day numbers arranged in a 7-column layout.
 * - Requirement 2.5: Show first 3 indicators + "+N más" overflow label.
 * - Requirement 2.6: Visually distinguish today with highlighted border/background.
 * - Requirement 2.7: Reduced opacity for out-of-month days.
 */

import { tokens } from '../../../design-system/tokens';
import { BookingIndicator } from './BookingIndicator';
import type { CalendarViewBooking } from '../calendarView.types';

// ============================================
// TYPES
// ============================================

export interface DayCellProps {
  /** Day number to display (1-31) */
  day: number;
  /** Whether this day belongs to the currently displayed month */
  isCurrentMonth: boolean;
  /** Whether this day is today */
  isToday: boolean;
  /** Whether this day is the focused selection (lista filtrada por día) */
  isSelected: boolean;
  /** Bookings that fall on this day */
  bookings: CalendarViewBooking[];
  /** Click en días del mes actual para filtrar la lista por ese día */
  onSelect?: () => void;
}

// ============================================
// CONSTANTS
// ============================================

/** Maximum number of booking indicators to display before showing overflow */
const MAX_VISIBLE_INDICATORS = 3;

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * DayCell — Renders a single day cell in the month grid.
 *
 * Shows the day number at the top, up to 3 BookingIndicator components,
 * and a "+N más" overflow label when bookings exceed 3.
 *
 * @example
 * ```tsx
 * <DayCell
 *   day={15}
 *   isCurrentMonth={true}
 *   isToday={true}
 *   bookings={[...]}
 * />
 * ```
 */
export function DayCell({ day, isCurrentMonth, isToday, isSelected, bookings, onSelect }: DayCellProps) {
  const visibleBookings = bookings.slice(0, MAX_VISIBLE_INDICATORS);
  const overflowCount = bookings.length - MAX_VISIBLE_INDICATORS;
  const interactive = Boolean(isCurrentMonth && onSelect);

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onSelect : undefined}
      onKeyDown={
        interactive ?
          (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect?.();
            }
          }
        : undefined
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacing[1],
        padding: tokens.spacing[1],
        minHeight: '5.5rem',
        borderRadius: tokens.borderRadius.md,
        border: isSelected
          ? `2px solid ${tokens.colors.primary[600]}`
          : isToday
            ? `2px solid ${tokens.colors.info.main}`
            : `1px solid ${tokens.colors.neutral[200]}`,
        backgroundColor: isSelected
          ? tokens.colors.primary[50]
          : isToday
            ? tokens.colors.info.light
            : tokens.colors.neutral[50],
        opacity: isCurrentMonth ? 1 : 0.4,
        transition: `background-color ${tokens.transitions.fast}`,
        cursor: interactive ? 'pointer' : 'default',
        outline: 'none',
      }}
    >
      {/* Day number */}
      <span
        style={{
          fontSize: tokens.typography.fontSize.sm,
          fontWeight: isToday
            ? tokens.typography.fontWeight.bold
            : tokens.typography.fontWeight.medium,
          color: isToday ? tokens.colors.info.dark : tokens.colors.neutral[700],
          lineHeight: tokens.typography.lineHeight.tight,
        }}
      >
        {day}
      </span>

      {/* Booking indicators (max 3) */}
      {visibleBookings.map((booking) => (
        <BookingIndicator
          key={booking.id}
          resourceName={booking.resourceName}
          status={booking.status}
          bookingOrigin={booking.bookingOrigin}
          otaChannel={booking.otaChannel}
          conversationChannel={booking.conversationChannel}
          customerName={booking.customerName}
          createdBy={booking.createdBy}
          alsoBlockedOnAirbnb={booking.alsoBlockedOnAirbnb}
        />
      ))}

      {/* Overflow label */}
      {overflowCount > 0 && (
        <span
          style={{
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.neutral[500],
            fontWeight: tokens.typography.fontWeight.medium,
            lineHeight: tokens.typography.lineHeight.tight,
          }}
        >
          +{overflowCount} más
        </span>
      )}
    </div>
  );
}

export default DayCell;
